import React, { HTMLAttributes, useEffect, useMemo, useRef } from 'react'
import { Header } from '../molecules/header'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { fetchHotelGuide } from '@/features/hotel/hotel-guide-slice'
import { isEmpty } from 'lodash'
import { Footer } from '../molecules/footer'
import { Toast } from '@/features/notification/toast-slice'
import { useNotification } from '@/hooks/use-notification'
import { AccountInfo, getAccountInfo } from '@/features/user/account-slice'
import { useHotelId } from '@/hooks/use-hotel-id'
import { fetchAsset } from '@/features/asset/assets-slice'
import { fetchDesign } from '@/features/asset/design-slice'
import { CUSTOM_BG, CustomDesignStyle } from './custom-design-style'
import { useTranslation } from 'react-i18next'
import { LanguageType } from '@/i18n'
import { reloadIfVersionHasChanged } from '@/libs/version-reload'
import { useCustomScript } from '@/hooks/use-custom-script'
import { useNavigate } from 'react-router-dom'

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<HTMLAttributes<LayoutProps>> = ({ children, style: parentStyle }) => {
  const auth = localStorage.getItem('access_token')
  const { Notification, openNotification } = useNotification()
  const { getHotelId } = useHotelId()
  const { i18n } = useTranslation()
  useCustomScript()
  const contentRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const toast = useAppSelector<Toast>(state => state.toast)
  const dispatch = useAppDispatch()

  const accountInfo = useAppSelector<AccountInfo>(state => state.accountInfo.user)
  const hotelGuide = useAppSelector(state => state.hotelGuide)
  const assetState = useAppSelector(state => state.assets)

  useEffect(() => {
    if (toast.message) openNotification(toast)
  }, [toast])

  useEffect(() => {
    const hotelId = getHotelId()
    if (!hotelId) {
      return
    }
    dispatch(fetchHotelGuide(hotelId))
    dispatch(fetchAsset({ hotelId }))
    dispatch(fetchDesign({ hotelId }))
    if (auth && isEmpty(accountInfo)) {
      dispatch(getAccountInfo())
    }
    reloadIfVersionHasChanged()
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (hotelGuide.errorStatus === 403) {
      navigate('/error-403')
    }
    if (hotelGuide.errorStatus === 404) {
      navigate('/error-404')
    }
    if (assetState.isFilled && assetState.assets.length === 0) {
      navigate('/error-403')
    }
  }, [assetState.isFilled, assetState.assets, hotelGuide.errorStatus])

  const hotelName = useMemo(
    () => (i18n.language === LanguageType.ja ? hotelGuide.facilityBasicInfo.hotelName : hotelGuide.facilityBasicInfo.hotelNameLatin),
    [hotelGuide, i18n.language],
  )

  return (
    <>
      <CustomDesignStyle />
      <div className={CUSTOM_BG} style={parentStyle}>
        {Notification}
        <div ref={contentRef}>
          {hotelName && <Header hotelName={hotelName} />}
          {children}
        </div>
        {contentRef.current && <Footer contentHeight={contentRef.current.getBoundingClientRect().height} />}
      </div>
    </>
  )
}
